import { useTranslation } from "@/i18n/use-t";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n";
import { cn } from "@/lib/utils";

/**
 * Minimal language toggle. Two languages → buttons, not a dropdown. The
 * detection chain in i18n/index.ts honours `?lang=` first so we don't bother
 * rewriting the URL on switch — localStorage capture is enough for repeat
 * visits, and the URL stays meaningful for sharing without hard-coding a
 * language preference into the link.
 */
export function LanguageSwitcher() {
  const { i18n, t } = useTranslation("common");
  const current = i18n.resolvedLanguage as SupportedLanguage | undefined;

  function set(lang: SupportedLanguage) {
    void i18n.changeLanguage(lang);
  }

  return (
    <nav
      aria-label={t("language.label")}
      className="flex items-center gap-1 text-sm"
    >
      {SUPPORTED_LANGUAGES.map((lang) => (
        <button
          type="button"
          key={lang}
          onClick={() => set(lang)}
          aria-pressed={current === lang}
          className={cn(
            "rounded-md px-2.5 py-1 transition-colors",
            current === lang
              ? "bg-accent font-medium text-accent-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <span className="uppercase">{lang}</span>
        </button>
      ))}
    </nav>
  );
}
